import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import { Token } from '../types';

/**
 * Imports token data from an Excel file
 * @param file The Excel file to import
 * @param existingMarketingColumns Array of existing marketing column names
 * @returns An object containing the imported tokens and any new marketing columns
 */
export const importFromExcel = async (
  file: File,
  existingMarketingColumns: string[]
): Promise<{
  tokens: Token[];
  newMarketingColumns: string[];
  errors?: string[];
}> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const errors: string[] = [];
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error('Failed to read file'));
          return;
        }
        
        // Parse the Excel file
        const workbook = XLSX.read(data, { type: 'binary' });
        
        // Get the first sheet
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(sheet) as Record<string, any>[];
        
        if (jsonData.length === 0) {
          reject(new Error('No data found in the Excel file'));
          return;
        }
        
        // Identify standard and marketing columns
        const standardColumns = ['Token Name', 'Website Traffic', 'Total Raised', 'Telegram', 'X (Twitter)', 'Instagram'];
        const marketingColumns = Object.keys(jsonData[0]).filter(
          key => !standardColumns.includes(key)
        );
        
        // Identify new marketing columns
        const newMarketingColumns = marketingColumns.filter(
          col => !existingMarketingColumns.includes(col)
        );
        
        // Convert Excel data to Token objects
        const tokens: Token[] = jsonData.map(row => {
          // Create a new token with standard fields
          const token: Partial<Token> = {
            id: uuidv4(),
            TokenName: row['Token Name'] || '',
            TokenWebsiteTraffic: row['Website Traffic'] || '',
            TokenTotalRaised: row['Total Raised'] || '',
            Telegram: row['Telegram'] || '',
            X: row['X (Twitter)'] || '',
            Instagram: row['Instagram'] || '',
          };
          
          // Add marketing columns
          marketingColumns.forEach(column => {
            if (row[column]) {
              // Convert comma-separated string to array
              if (typeof row[column] === 'string') {
                token[column] = row[column].split(',').map((item: string) => item.trim()).filter(Boolean);
              } else {
                token[column] = [String(row[column])];
              }
            } else {
              token[column] = [];
            }
          });
          
          return token as Token;
        });
        
        resolve({
          tokens,
          newMarketingColumns,
          errors: errors.length > 0 ? errors : undefined
        });
      } catch (error) {
        console.error('Error importing Excel file:', error);
        reject(new Error('Failed to parse Excel file. Please make sure it has the correct format.'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    // Read the file as binary
    reader.readAsBinaryString(file);
  });
}; 